/**
 * Element Registrations
 * 
 * This file registers all available element types with their definitions.
 * These elements can be used in templates for rendering content.
 */
import Konva from "konva";
import { CircleElement, ImageElement, RectElement, TextElement, SvgElement, zCircleElement, zImageElement, zRectElement, zTextElement, zSvgElement } from "../../../core/template.schemas";
import { resolveStaticOrDynamicWithPreview, resolveTextContentWithPreview } from "../../../core/runtime/resolve";
import { ElementRegistry } from "./elementRegistry";

/**
 * Register all standard element types
 */
export function registerElements(registry: ElementRegistry): void {
  // Register rectangle element
  registry.register<RectElement>({
    type: 'rect',
    zodSchema: zRectElement,
    defaultProps: {
      id: 'default-uuid',
      type: 'rect',
      x: 50,
      y: 50,
      width: 200,
      height: 100,
      rotation: 0,
      zIndex: 1,
      locked: false,
      visible: true,
      opacity: 1,
      effects: [],
      fill: { 
        kind: 'solid', 
        color: { mode: 'static', mediaType: 'color', staticValue: '#CCCCCC' }
      },
    },
    konvaRenderer: (props, ctx) => {
      // Resolve dynamic colors with preview fallback
      const resolveContext = ctx?.resolveContext || { selected: {}, overrides: {} };
      
      let fillColor: string | undefined;
      if (props.fill?.kind === 'solid') {
        fillColor = resolveStaticOrDynamicWithPreview(props.fill.color, resolveContext);
      }
      
      let strokeColor: string | undefined;
      if (props.stroke?.color) {
        strokeColor = resolveStaticOrDynamicWithPreview(props.stroke.color, resolveContext);
      }

      const config: Konva.RectConfig = {
        id: props.id,
        x: props.x,
        y: props.y,
        width: props.width,
        height: props.height,
        rotation: props.rotation,
        opacity: (props.opacity ?? 1) * (props.fill?.kind === 'solid' ? (props.fill.opacity || 1) : 1),
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: props.stroke?.width,
        cornerRadius: props.cornerRadius,
        visible: props.visible,
        draggable: !props.locked,
      };
      return [config];
    },
    ui: {
      icon: 'square',
      color: 'blue',
      displayName: 'Rechteck',
      description: 'Rechteckige Form für Hintergründe und geometrische Designs'
    }
  });

  // Register circle/ellipse element
  registry.register<CircleElement>({
    type: 'circle',
    zodSchema: zCircleElement,
    defaultProps: {
      id: 'default-uuid',
      type: 'circle',
      x: 200,
      y: 200,
      width: 200,
      height: 200,
      rotation: 0,
      zIndex: 1,
      locked: false,
      visible: true,
      opacity: 1,
      effects: [],
      fill: { 
        kind: 'solid', 
        color: { mode: 'static', mediaType: 'color', staticValue: '#FFCC00' }
      },
    },
    konvaRenderer: (props, ctx) => {
      // Resolve dynamic colors with preview fallback
      const resolveContext = ctx?.resolveContext || { selected: {}, overrides: {} };
      
      let fillColor: string | undefined;
      if (props.fill?.kind === 'solid') {
        fillColor = resolveStaticOrDynamicWithPreview(props.fill.color, resolveContext);
      }
      
      let strokeColor: string | undefined;
      if (props.stroke?.color) {
        strokeColor = resolveStaticOrDynamicWithPreview(props.stroke.color, resolveContext);
      }

      const config: Konva.EllipseConfig = {
        id: props.id,
        x: props.x,
        y: props.y,
        radiusX: props.width / 2,
        radiusY: props.height / 2,
        rotation: props.rotation,
        opacity: (props.opacity ?? 1) * (props.fill?.kind === 'solid' ? (props.fill.opacity || 1) : 1),
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: props.stroke?.width,
        visible: props.visible,
        draggable: !props.locked,
      };
      return [config];
    },
    ui: {
      icon: 'circle',
      color: 'yellow',
      displayName: 'Kreis',
      description: 'Kreisförmige Form für Logos, Avatare und runde Designs'
    }
  });

  // Register text element
  registry.register<TextElement>({
    type: 'text',
    zodSchema: zTextElement,
    defaultProps: {
      id: 'default-uuid',
      type: 'text',
      x: 50,
      y: 50,
      width: 200,
      height: 50,
      rotation: 0,
      zIndex: 1,
      locked: false,
      visible: true,
      opacity: 1,
      content: {
        mode: 'static',
        mediaType: 'text',
        staticValue: 'Sample Text'
      },
      fontFamily: 'Poppins',
      fontWeight: '400',
      fontStyle: 'normal',
      fontSizing: {
        mode: 'manual',
        fontSize: 32,
      },
      effects: [],
      color: { mode: 'static', mediaType: 'color', staticValue: '#000000' },
      align: { h: 'left', v: 'top' },
      lineHeight: 1.2,
    },
    konvaRenderer: (props, ctx) => {
      // Resolve the content and color values with preview fallback
      const resolveContext = ctx?.resolveContext || { selected: {}, overrides: {} };
      const resolvedText = resolveTextContentWithPreview(props.content, resolveContext);
      const resolvedColor = resolveStaticOrDynamicWithPreview(props.color, resolveContext);
      
      const konvaConfig: Konva.TextConfig = {
        id: props.id,
        x: props.x,
        y: props.y,
        width: props.width,
        height: props.height,
        rotation: props.rotation,
        text: resolvedText || '',
        fontFamily: props.fontFamily,
        // fontSize is set dynamically below
        fontStyle: `${props.fontStyle || 'normal'} ${props.fontWeight || '400'}`,
        fill: resolvedColor,
        align: props.align.h,
        verticalAlign: props.align.v === 'center' ? 'middle' : props.align.v,
        lineHeight: props.lineHeight || 1.2,
        opacity: props.opacity,
        visible: props.visible,
        draggable: !props.locked,
        wrap: 'word',
        ellipsis: props.fontSizing?.mode === 'manual' ? !!props.fontSizing?.ellipsis : false,
      };

      // Apply font sizing logic
      if (props.fontSizing?.mode === 'fit') {
        // --- Dynamic font size calculation for 'fit' mode ---
        const textNode = new Konva.Text({
          text: resolvedText || '',
          fontFamily: konvaConfig.fontFamily,
          fontStyle: konvaConfig.fontStyle,
          align: konvaConfig.align,
          verticalAlign: konvaConfig.verticalAlign,
          lineHeight: konvaConfig.lineHeight,
          wrap: 'word',
          width: props.width,
        });

        let minFontSize = 1;
        let maxFontSize = props.height + 10; // Start slightly larger than the container height
        let bestSize = minFontSize;

        // Binary search for the optimal font size
        while (minFontSize <= maxFontSize) {
          const midFontSize = Math.floor((minFontSize + maxFontSize) / 2);
          if (midFontSize < 1) break;
          textNode.fontSize(midFontSize);

          if (textNode.height() <= props.height) {
            bestSize = midFontSize;
            minFontSize = midFontSize + 1;
          } else {
            maxFontSize = midFontSize - 1;
          }
        }

        konvaConfig.fontSize = bestSize;
        konvaConfig.wrap = 'word';
        konvaConfig.ellipsis = false; // No ellipsis in fit mode
      } else { // 'manual' mode or fallback
        konvaConfig.fontSize = props.fontSizing?.fontSize ?? 32;
        konvaConfig.wrap = 'word';
        konvaConfig.ellipsis = !!props.fontSizing?.ellipsis;
      }

      return [konvaConfig];
    },
    ui: {
      icon: 'type',
      color: 'green',
      displayName: 'Text',
      description: 'Textfeld für Überschriften, Namen und dynamische Inhalte'
    }
  });

  // Register image element
  registry.register<ImageElement>({
    type: 'image',
    zodSchema: zImageElement,
    defaultProps: {
      id: 'default-uuid',
      type: 'image',
      x: 50,
      y: 50,
      width: 200,
      height: 200,
      rotation: 0,
      zIndex: 1,
      locked: false,
      visible: true,
      opacity: 1,
      effects: [],
      source: {
        mode: 'static',
        mediaType: 'image',
        format: { fit: 'contain' },
        staticValue: {}
      },
      clipRadius: 0
    },
    konvaRenderer: (props, ctx) => {
      const resolveContext = ctx?.resolveContext || { selected: {}, overrides: {} };
      const resolvedSource = resolveStaticOrDynamicWithPreview(props.source, resolveContext);
      
      const config: Konva.ImageConfig = {
        id: props.id,
        x: props.x,
        y: props.y,
        width: props.width,
        height: props.height,
        rotation: props.rotation,
        opacity: props.opacity,
        visible: props.visible,
        draggable: !props.locked,
        image: resolvedSource ? new Image(200, 200) : undefined
      };
      return [config];
    },
    ui: {
      icon: 'image',
      color: 'purple',
      displayName: 'Bild',
      description: 'Bildfeld für Fotos, Logos und grafische Inhalte'
    }
  });

  // Register SVG element
  registry.register<SvgElement>({
    type: 'svg',
    zodSchema: zSvgElement,
    defaultProps: {
      id: 'default-uuid',
      type: 'svg',
      x: 50,
      y: 50,
      width: 200,
      height: 200,
      rotation: 0,
      zIndex: 1,
      locked: false,
      visible: true,
      opacity: 1,
      effects: [],
      source: {
        mode: 'static',
        mediaType: 'image',
        format: { fit: 'contain' },
        staticValue: {}
      },
      colorMapping: {},
      fillMode: 'original'
    },
    konvaRenderer: (props, ctx) => {
      const config: Konva.ImageConfig = {
        id: props.id,
        x: props.x,
        y: props.y,
        width: props.width,
        height: props.height,
        rotation: props.rotation,
        opacity: props.opacity,
        visible: props.visible,
        draggable: !props.locked,
        image: undefined,
      };
      return [config];
    },
    ui: {
      icon: 'component',
      color: 'orange',
      displayName: 'SVG / Icon',
      description: 'Vektorgrafik mit anpassbaren Farben'
    }
  });
}